import { z } from "zod";

/** Declared provenance only; never authority for actor, tenant or business validation. */
export const VoluntaryEmailMetadataSchema = z
  .object({
    declared_channel: z.enum(["ui", "api", "cli", "mcp"]),
    received_at_declared: z
      .string()
      .datetime({ offset: true })
      // Date canonicalization must not silently discard sub-millisecond input.
      .refine((value) => !/\.\d{4}/.test(value), "Use at most millisecond precision")
      .refine((value) => Number.isFinite(Date.parse(value)), "Invalid timestamp")
      .transform((value) => new Date(value).toISOString())
      .nullable()
      .optional()
      .transform((value) => value ?? null),
  })
  .strict();

export type VoluntaryEmailMetadata = z.infer<typeof VoluntaryEmailMetadataSchema>;

/** Raw message/rfc822 body; these query fields never carry identity or authority. */
export const VoluntaryEmailImportQuerySchema = VoluntaryEmailMetadataSchema.extend({
  idempotency_key: z.string().uuid(),
}).strict();
export const VoluntaryEmailAdmissionSchema = z
  .object({
    admission_id: z.string().uuid(),
    intake_event_id: z.string().uuid(),
    interpretation: z.literal("unverified"),
  })
  .strict();
export const VoluntaryEmailDetailSchema = VoluntaryEmailAdmissionSchema.extend({
  /** Server provenance. Optional for compatibility with older API responses. */
  origin: z.enum(["voluntary_email", "mailgun_json"]).optional(),
  provider_context: z
    .object({
      project_id: z.string().uuid(),
      quote_request_id: z.string().uuid(),
      recipient_id: z.string().uuid(),
    })
    .strict()
    .optional(),
  subject: z.string(),
  text_preview: z.string(),
  text_truncated: z.boolean(),
  parts: z
    .array(
      z
        .object({
          part_index: z.number().int().min(0).max(10),
          declared_name: z.string(),
          declared_mime: z.string(),
          representation: z.enum(["original", "decoded", "normalized_calendar"]),
          size_bytes: z
            .number()
            .int()
            .min(0)
            .max(10 * 1024 * 1024),
        })
        .strict(),
    )
    .max(11),
}).strict();
export type VoluntaryEmailAdmission = z.infer<typeof VoluntaryEmailAdmissionSchema>;
export type VoluntaryEmailDetail = z.infer<typeof VoluntaryEmailDetailSchema>;
